(() => {
  const TRIGGER = '[data-combined-product-url]';
  const SCRIPT_TYPES = [
    '',
    'text/javascript',
    'application/javascript',
    'module'
  ];

  const modifiedClick = (event) => (
    event.metaKey
    || event.ctrlKey
    || event.shiftKey
    || event.altKey
    || event.button !== 0
  );

  function parseHTML(html) {
    const template = document.createElement('template');
    template.innerHTML = html;
    return template;
  }

  async function fetchProductPage(url) {
    const response = await fetch(url, {
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error(
        `Product fetch failed: ${response.status}`
      );
    }

    return parseHTML(await response.text());
  }

  function loadExternalResources(root) {
    root
      .querySelectorAll('link[rel="stylesheet"]')
      .forEach((element) => {
        const href = element.getAttribute('href');

        if (
          href
          && !document.querySelector(
            `link[rel="stylesheet"][href="${href}"]`
          )
        ) {
          document.head.appendChild(
            element.cloneNode(true)
          );
        }
      });

    root
      .querySelectorAll('script[src]')
      .forEach((element) => {
        const src = element.getAttribute('src');

        if (
          !src
          || document.querySelector(`script[src="${src}"]`)
        ) {
          return;
        }

        const script = document.createElement('script');

        Array.from(element.attributes).forEach(
          (attribute) => {
            script.setAttribute(
              attribute.name,
              attribute.value
            );
          }
        );

        document.head.appendChild(script);
      });
  }

  function extractInlineScripts(root) {
    return Array.from(
      root.querySelectorAll('script:not([src])')
    )
      .filter((script) => {
        const type = (
          script.getAttribute('type') || ''
        ).toLowerCase();

        return SCRIPT_TYPES.includes(type);
      })
      .map((script) => {
        const saved = {
          attributes: Array.from(
            script.attributes
          ).map((attribute) => ({
            name: attribute.name,
            value: attribute.value
          })),
          text: script.textContent
        };

        script.remove();

        return saved;
      });
  }

  async function executeSavedScripts(
    container,
    scripts
  ) {
    if (container.querySelector('variant-picker')) {
      await customElements.whenDefined(
        'variant-picker'
      );
    }

    scripts.forEach((saved) => {
      const script = document.createElement('script');

      saved.attributes.forEach((attribute) => {
        script.setAttribute(
          attribute.name,
          attribute.value
        );
      });

      const type = (
        script.getAttribute('type') || ''
      ).toLowerCase();

      script.textContent = type === 'module'
        ? saved.text
        : `{
            ${saved.text}
          }`;

      container.appendChild(script);
    });
  }

  function getCurrentVariantId(root) {
    return root.querySelector(
      [
        '.section-main-product',
        '.js-product-form',
        '[name="id"]'
      ].join(' ')
      + ', '
      + [
        '.quickbuy-content',
        '.js-product-form',
        '[name="id"]'
      ].join(' ')
      + ', .js-product-form [name="id"]'
    )?.value || null;
  }

  function updateDocumentMeta(
    template,
    productUrl,
    root
  ) {
    const title = template.content.querySelector(
      'title'
    );

    if (title?.textContent) {
      document.title = title.textContent;
    }

    const historyUrl = new URL(
      productUrl,
      window.location.origin
    );

    const variantId = getCurrentVariantId(root);

    if (variantId) {
      historyUrl.searchParams.set(
        'variant',
        variantId
      );
    }

    window.history.pushState(
      {},
      '',
      `${historyUrl.pathname}${historyUrl.search}${historyUrl.hash}`
    );

    return variantId;
  }

  function convertToQuickbuyContent(
    content,
    productUrl
  ) {
    content
      .querySelectorAll('.more')
      .forEach((element) => {
        element.href = productUrl;
      });

    content
      .querySelectorAll('.product-title')
      .forEach((element) => {
        element.innerHTML = `
          <a href="${productUrl}">
            ${element.innerHTML}
          </a>
        `;
      });

    content
      .querySelectorAll('.show-gallery')
      .forEach((element) => {
        element.classList.remove(
          'show-gallery'
        );

        element.href = productUrl;
      });

    content
      .querySelectorAll('.not-in-quickbuy')
      .forEach((element) => {
        element.remove();
      });

    content
      .querySelectorAll('.only-in-quickbuy')
      .forEach((element) => {
        element.classList.remove(
          'only-in-quickbuy'
        );
      });

    content
      .querySelectorAll(
        '.sticky-content-container'
      )
      .forEach((element) => {
        element.classList.remove(
          'sticky-content-container'
        );
      });

    content
      .querySelectorAll(
        'pickup-availability, noscript'
      )
      .forEach((element) => {
        element.remove();
      });

    content
      .querySelectorAll(
        'variant-picker[data-update-url="true"]'
      )
      .forEach((element) => {
        element.dataset.updateUrl = 'false';
      });

    content
      .querySelectorAll(
        '.media-gallery .main-image carousel-slider'
      )
      .forEach((element) => {
        element.classList.remove(
          'mobile-only'
        );
      });

    content
      .querySelectorAll(
        '.product-media-collage'
      )
      .forEach((element) => {
        element.remove();
      });

    content
      .querySelectorAll(
        '.media-gallery .thumbnails'
      )
      .forEach((element) => {
        element.classList.remove(
          'mobile-only'
        );
      });

    [
      'media-gallery--layout-carousel-beside',
      'media-gallery--layout-columns-1',
      'media-gallery--layout-columns-2',
      'media-gallery--layout-collage-1',
      'media-gallery--layout-collage-2'
    ].forEach((className) => {
      content
        .querySelectorAll(`.${className}`)
        .forEach((element) => {
          element.classList.remove(className);

          element.classList.add(
            'media-gallery--layout-carousel-under'
          );
        });
    });
  }

  function parseMoneyToCents(text) {
    const value = Number.parseFloat(
      String(text || '')
        .replace(/\s/g, '')
        .replace(/,/g, '')
        .replace(/[^0-9.-]/g, '')
    );

    return Number.isFinite(value)
      ? Math.round(value * 100)
      : null;
  }

  function formatMoney(cents) {
    const currency =
      window.Shopify?.currency?.active
      || 'USD';

    const locale =
      document.documentElement.lang
      || 'en-US';

    try {
      return new Intl.NumberFormat(
        locale,
        {
          style: 'currency',
          currency
        }
      ).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  function getProductRootFromForm(form) {
    return form.closest(
      'product-form, .quickbuy-content, .js-product, .section-main-product'
    ) || document;
  }

  function getFormFromVariantEvent(event) {
    const detailForm = event.detail?.form;

    if (
      detailForm
      && detailForm.matches
      && detailForm.matches('form.js-product-form')
    ) {
      return detailForm;
    }

    if (
      detailForm
      && detailForm.querySelector
    ) {
      const nestedForm = detailForm.querySelector(
        'form.js-product-form'
      );

      if (nestedForm) {
        return nestedForm;
      }
    }

    const target = event.target instanceof Element
      ? event.target
      : null;

    const root = target?.closest(
      'product-form, .quickbuy-content, .js-product, .section-main-product, .quickbuy-container, #content'
    ) || document;

    return root.querySelector(
      'form.js-product-form'
    );
  }

  function getVariantPriceCents(variant) {
    const price = Number(variant?.price);

    return Number.isFinite(price)
      ? price
      : null;
  }

  function getBulkDiscountUnitPrice(
    form,
    quantity,
    fallbackUnitPrice
  ) {
    const root = getProductRootFromForm(form);

    const columns = Array.from(
      root.querySelectorAll(
        '.product-bulk-discount-table .bulkPriceColumns .bulkPriceCol'
      )
    ).filter((column) => (
      !column.classList.contains('bulkHeadingCol')
    ));

    if (!columns.length) {
      return fallbackUnitPrice;
    }

    let matchedMinQuantity = 0;
    let matchedUnitPrice = fallbackUnitPrice;

    columns.forEach((column) => {
      const quantityCell = column.querySelector(
        '.bulkQuantityCol[data-value]'
      );

      const priceCell = column.querySelector(
        '.bulkPriceColinner'
      );

      const minQuantity = Number.parseInt(
        quantityCell?.dataset.value || '',
        10
      );

      const unitPrice = parseMoneyToCents(
        priceCell?.textContent || ''
      );

      if (
        Number.isFinite(minQuantity)
        && unitPrice !== null
        && quantity >= minQuantity
        && minQuantity >= matchedMinQuantity
      ) {
        matchedMinQuantity = minQuantity;
        matchedUnitPrice = unitPrice;
      }
    });

    return matchedUnitPrice;
  }

  function updateBulkDiscountHighlight(
    form,
    quantity
  ) {
    if (!form) return;

    const root = getProductRootFromForm(form);

    const table = root.querySelector(
      '.product-bulk-discount-table'
    );

    if (!table) return;

    const columns = Array.from(
      table.querySelectorAll(
        '.bulkPriceColumns .bulkPriceCol'
      )
    ).filter((column) => (
      !column.classList.contains('bulkHeadingCol')
    ));

    if (!columns.length) return;

    let activeColumn = null;
    let activeMinQuantity = 0;

    columns.forEach((column) => {
      const quantityCell = column.querySelector(
        '.bulkQuantityCol[data-value]'
      );

      const minQuantity = Number.parseInt(
        quantityCell?.dataset.value || '',
        10
      );

      if (
        Number.isFinite(minQuantity)
        && quantity >= minQuantity
        && minQuantity >= activeMinQuantity
      ) {
        activeColumn = column;
        activeMinQuantity = minQuantity;
      }
    });

    columns.forEach((column) => {
      column.classList.remove('highlighted');

      column
        .querySelectorAll(
          '.bulkQuantityCol, .bulkSaveCol, .bulkPriceColinner'
        )
        .forEach((cell) => {
          cell.classList.remove('active');
        });
    });

    if (!activeColumn) return;

    activeColumn.classList.add('highlighted');

    activeColumn
      .querySelectorAll(
        '.bulkQuantityCol, .bulkSaveCol, .bulkPriceColinner'
      )
      .forEach((cell) => {
        cell.classList.add('active');
      });

    const nextTierMessage = table.querySelector(
      '.next-tier-message'
    );

    const nextColumn = columns.find((column) => {
      const quantityCell = column.querySelector(
        '.bulkQuantityCol[data-value]'
      );

      const minQuantity = Number.parseInt(
        quantityCell?.dataset.value || '',
        10
      );

      return (
        Number.isFinite(minQuantity)
        && minQuantity > quantity
      );
    });

    if (nextColumn) {
      const quantityCell = nextColumn.querySelector(
        '.bulkQuantityCol[data-value]'
      );

      const nextQuantity = Number.parseInt(
        quantityCell?.dataset.value || '',
        10
      );

      const saveText = nextColumn.querySelector(
        '.bulkSaveCol'
      )?.textContent.trim();

      if (
        Number.isFinite(nextQuantity)
        && saveText
      ) {
        const remaining = nextQuantity - quantity;

        const message = `Add ${remaining} more to save ${saveText} on your order!`;

        if (nextTierMessage) {
          nextTierMessage.textContent = message;
          nextTierMessage.style.display = 'block';
        } else {
          const messageDiv = document.createElement('div');
          messageDiv.className = 'next-tier-message';
          messageDiv.textContent = message;
          table.appendChild(messageDiv);
        }
      }
    } else if (nextTierMessage) {
      nextTierMessage.style.display = 'none';
    }
  }

  function initializeButtonPrice(form) {
    const current = form.querySelector(
      '.add-to-cart .price__default .price__current'
    );

    if (
      !current
      || current.dataset.unitPriceCents
    ) {
      return;
    }

    const cents = parseMoneyToCents(
      current.textContent
    );

    if (cents !== null) {
      current.dataset.unitPriceCents =
        String(cents);
    }

    const compare = form.querySelector(
      '.add-to-cart .price__default .price__was'
    );

    if (
      !compare
      || compare.dataset.unitPriceCents
    ) {
      return;
    }

    const compareCents = parseMoneyToCents(
      compare.textContent
    );

    if (compareCents !== null) {
      compare.dataset.unitPriceCents =
        String(compareCents);
    }
  }

  function updateButtonPrice(
    form,
    variant = null
  ) {
    if (!form) return;

    const quantityInput = form.querySelector(
      '[name="quantity"]'
    );

    const current = form.querySelector(
      '.add-to-cart .price__default .price__current'
    );

    if (!quantityInput || !current) {
      return;
    }

    initializeButtonPrice(form);

    const variantPrice = getVariantPriceCents(
      variant
    );

    if (variantPrice !== null) {
      current.dataset.unitPriceCents =
        String(variantPrice);
    }

    const unitPrice = Number.parseInt(
      current.dataset.unitPriceCents || '',
      10
    );

    if (!Number.isFinite(unitPrice)) {
      return;
    }

    const quantity = Math.max(
      1,
      Number.parseInt(
        quantityInput.value,
        10
      ) || 1
    );

    const discountedUnitPrice = getBulkDiscountUnitPrice(
      form,
      quantity,
      unitPrice
    );

    const totalPrice = discountedUnitPrice * quantity;

    current.textContent = formatMoney(totalPrice);

    updateBulkDiscountHighlight(
      form,
      quantity
    );

    const compare = form.querySelector(
      '.add-to-cart .price__default .price__was'
    );

    if (!compare) return;

    if (variant) {
      const compareAt =
        Number(variant.compare_at_price)
        || 0;

      compare.dataset.unitPriceCents =
        compareAt > Number(variant.price)
          ? String(compareAt)
          : '';
    }

    const compareUnit = Number.parseInt(
      compare.dataset.unitPriceCents || '',
      10
    );

    compare.textContent =
      Number.isFinite(compareUnit)
        ? formatMoney(
          compareUnit * quantity
        )
        : '';
  }

  function initializePrices(
    root = document
  ) {
    root
      .querySelectorAll(
        'form.js-product-form'
      )
      .forEach((form) => {
        initializeButtonPrice(form);
        updateButtonPrice(form);
      });
  }

  async function swapPdpProduct(
    productUrl
  ) {
    const content = document.querySelector(
      '#content'
    );

    if (!content) {
      window.location.href = productUrl;
      return;
    }

    content.classList.add(
      'combined-products-loading'
    );

    try {
      const template =
        await fetchProductPage(productUrl);

      const replacement =
        template.content.querySelector(
          '#content'
        );

      if (!replacement) {
        window.location.href = productUrl;
        return;
      }

      loadExternalResources(
        template.content
      );

      const scripts =
        extractInlineScripts(replacement);

      content.innerHTML =
        replacement.innerHTML;

      await executeSavedScripts(
        content,
        scripts
      );

      initializePrices(content);

      const variantId = updateDocumentMeta(
        template,
        productUrl,
        content
      );

      document.dispatchEvent(
        new CustomEvent(
          'combined-product:changed',
          {
            bubbles: true,
            detail: {
              productUrl,
              variantId
            }
          }
        )
      );

      content.dispatchEvent(
        new CustomEvent(
          'on:product:swapped',
          {
            bubbles: true,
            detail: {
              url: productUrl,
              variantId
            }
          }
        )
      );
    } finally {
      content.classList.remove(
        'combined-products-loading'
      );
    }
  }

  async function swapQuickbuyProduct(
    trigger,
    productUrl
  ) {
    const quickbuy = trigger.closest(
      '.quickbuy-container'
    );

    const container =
      quickbuy?.querySelector('.inner');

    if (!quickbuy || !container) {
      window.location.href = productUrl;
      return;
    }

    container.innerHTML = `
      <div class="loading-spinner"></div>
    `;

    const template =
      await fetchProductPage(productUrl);

    let replacement = null;

    if (
      window.theme
      ?.quickbuy
      ?.transformProductPage
    ) {
      replacement =
        theme.quickbuy.transformProductPage(
          template.content,
          productUrl
        );
    } else {
      replacement =
        template.content
          .querySelector(
            '.quickbuy-content'
          )
          ?.cloneNode(true);

      if (replacement) {
        loadExternalResources(
          template.content
        );

        convertToQuickbuyContent(
          replacement,
          productUrl
        );
      }
    }

    if (!replacement) {
      window.location.href = productUrl;
      return;
    }

    const scripts =
      extractInlineScripts(replacement);

    container.innerHTML = '';
    container.appendChild(replacement);

    await executeSavedScripts(
      container,
      scripts
    );

    initializePrices(container);

    if (
      window.theme
      ?.manuallyLoadImages
    ) {
      theme.manuallyLoadImages(
        container
      );
    }

    if (
      window.theme
      ?.quickbuy
      ?.debouncedQuickbuyResize
    ) {
      theme.quickbuy
        .debouncedQuickbuyResize(
          container
        );
    }

    quickbuy.dispatchEvent(
      new CustomEvent(
        'on:quickbuy:after-open',
        {
          bubbles: true
        }
      )
    );

    quickbuy.dispatchEvent(
      new CustomEvent(
        'on:product:swapped',
        {
          bubbles: true,
          detail: {
            url: productUrl,
            variantId:
              getCurrentVariantId(
                container
              )
          }
        }
      )
    );
  }

  function scheduleVariantPriceAndBulkRefresh(
    form,
    variant
  ) {
    const refresh = () => {
      updateButtonPrice(
        form,
        variant
      );
    };

    requestAnimationFrame(refresh);

    /*
     * The original bulk discount script can rebuild the table
     * after the variant state changes, so re-apply the price
     * and highlighted tier after those delayed DOM updates.
     */
    [100, 300, 650, 900].forEach((delay) => {
      setTimeout(refresh, delay);
    });
  }

  /*
   * Plus/minus quantity buttons.
   * Allow the theme to update the input first.
   */
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest(
        'quantity-wrapper [data-quantity]'
      );

      if (!button) return;

      const form = button.closest(
        'form.js-product-form'
      );

      requestAnimationFrame(() => {
        updateButtonPrice(form);
      });
    }
  );

  /*
   * Manual typing.
   */
  document.addEventListener(
    'input',
    (event) => {
      if (
        !event.target.matches(
          'form.js-product-form [name="quantity"]'
        )
      ) {
        return;
      }

      updateButtonPrice(
        event.target.closest(
          'form.js-product-form'
        )
      );
    }
  );

  /*
   * Theme and app-driven quantity changes.
   */
  document.addEventListener(
    'change',
    (event) => {
      if (
        !event.target.matches(
          'form.js-product-form [name="quantity"]'
        )
      ) {
        return;
      }

      updateButtonPrice(
        event.target.closest(
          'form.js-product-form'
        )
      );
    }
  );

  /*
   * Real variant changes.
   */
  document.addEventListener(
    'on:variant:change',
    (event) => {
      const variant = event.detail?.variant;
      const form = getFormFromVariantEvent(event);

      if (!form || !variant) {
        return;
      }

      const idInput = form.querySelector('[name="id"]');

      if (idInput && variant.id) {
        idInput.value = variant.id;
      }

      scheduleVariantPriceAndBulkRefresh(
        form,
        variant
      );
    },
    true
  );

  /*
   * Combined product links.
   */
  document.addEventListener(
    'click',
    async (event) => {
      const trigger = event.target.closest(
        TRIGGER
      );

      if (
        !trigger
        || modifiedClick(event)
      ) {
        return;
      }

      event.preventDefault();

      if (
        trigger.classList.contains(
          'is-active'
        )
      ) {
        return;
      }

      const productUrl =
        trigger.dataset.combinedProductUrl
        || trigger.href;

      const wrapper = trigger.closest(
        '[data-combined-product-options]'
      );

      if (!productUrl) return;

      try {
        wrapper?.classList.add(
          'is-loading'
        );

        if (
          trigger.closest(
            '.quickbuy-container'
          )
        ) {
          await swapQuickbuyProduct(
            trigger,
            productUrl
          );
        } else {
          await swapPdpProduct(
            productUrl
          );
        }
      } catch (error) {
        console.warn(
          'Combined product swap failed:',
          error
        );

        window.location.href =
          productUrl;
      } finally {
        wrapper?.classList.remove(
          'is-loading'
        );
      }
    }
  );

  initializePrices(document);
})();